import React, { useEffect, useRef, useCallback } from 'react'
import Hls from 'hls.js'
import { Box } from '@mui/material'

interface VideoPlayerProps {
  src: string
  onProgress?: (position: number, duration: number) => void
  initialPosition?: number
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, onProgress, initialPosition = 0 }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const progressTimerRef = useRef<ReturnType<typeof setInterval>>(undefined)
  const visibilityChangeRef = useRef<undefined | (() => void)>(undefined)
  const lastReportedProgressRef = useRef<number>(-1)

  const cleanup = useCallback(() => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current)
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null }
    if (visibilityChangeRef.current) {
      document.removeEventListener('visibilitychange', visibilityChangeRef.current)
      visibilityChangeRef.current = undefined
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    cleanup()

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 60,
        maxMaxBufferLength: 600,
        enableWorker: true,
        // Reduce fragment loading pressure
        fragLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 60000,
            timeoutRetry: { maxNumRetry: 3, retryDelayMs: 500, maxRetryDelayMs: 2000 },
            errorRetry: { maxNumRetry: 3, retryDelayMs: 500, maxRetryDelayMs: 2000 }
          }
        }
      })
      hlsRef.current = hls
      hls.loadSource(src)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (initialPosition > 0) video.currentTime = initialPosition
        video.play().catch(() => {})
      })
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError()
              break
            default:
              cleanup()
              break
          }
        }
      })
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src
      video.addEventListener('loadedmetadata', () => {
        if (initialPosition > 0) video.currentTime = initialPosition
        video.play().catch(() => {})
      })
    }

    const reportProgress = () => {
      if (!onProgress || !video.duration || video.currentTime <= 0) return

      const progress = video.currentTime / video.duration
      const progressPercent = Math.round(progress * 100)

      // Only report if progress changed by at least 5% or first time
      const lastProgress = lastReportedProgressRef.current
      if (Math.abs(progressPercent - lastProgress) >= 5 || lastProgress === -1) {
        lastReportedProgressRef.current = progressPercent
        onProgress(video.currentTime, video.duration)
      }
    }

    if (onProgress) {
      // Use longer interval - 30 seconds between reports (was 5s)
      progressTimerRef.current = setInterval(reportProgress, 30000)
      // Initial report after short delay
      setTimeout(reportProgress, 2000)
    }

    // Handle visibility change to prevent stutter when returning to tab
    visibilityChangeRef.current = () => {
      if (document.hidden) {
        // Tab is hidden - stop progress reporting
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current)
          progressTimerRef.current = undefined
        }
        // Do NOT call stopLoad() or detach - let HLS maintain state
      } else {
        // Tab is visible again - just resume progress reporting
        if (!progressTimerRef.current && onProgress) {
          progressTimerRef.current = setInterval(reportProgress, 30000)
        }
        // Resume playback if paused
        if (video.paused && video.ended === false) {
          video.play().catch(() => {})
        }
        // Do NOT call attachMedia() or startLoad() - this would reset position
      }
    }

    document.addEventListener('visibilitychange', visibilityChangeRef.current)

    return cleanup
  }, [src, initialPosition, onProgress, cleanup])

  return (
    <Box sx={{ width: '100%', bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
      <video
        ref={videoRef}
        controls
        playsInline
        style={{ width: '100%', maxHeight: '80vh', display: 'block' }}
      />
    </Box>
  )
}

export default VideoPlayer
